import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Package, MapPin, Truck, CheckCircle, Clock, AlertCircle, Copy, FileDown, Ban, Printer, X, Mail, Phone, XCircle, RotateCcw } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import orderService from '../../services/order.service';
import { sanitizeError } from '../../utils/errorUtils';
import { generateInvoice } from '../../utils/generateInvoice';

function OrderDetailsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('id');
  
  const { getOrderById, trackOrder, loading, error } = useOrders();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [nimbusBusy, setNimbusBusy] = useState(null);
  const [nimbusNotice, setNimbusNotice] = useState(null);

  // OTP States
  const [otpModal, setOtpModal] = useState({ show: false, otp: '' });
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const orderData = await getOrderById(orderId);
      setOrder(orderData);
    } catch (err) {
      console.error('Error loading order:', err);
    }
  };

  const handleTrackOrder = async () => {
    if (!orderId) return;
    
    setLoadingTracking(true);
    try {
      const trackingData = await trackOrder(orderId);
      setTracking(trackingData);
    } catch (err) {
      console.error('Error tracking order:', err);
    } finally {
      setLoadingTracking(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'picked_up':
      case 'in_transit':
      case 'out_for_delivery':
        return <Truck className="w-5 h-5 text-blue-600" />;
      case 'pending':
      case 'confirmed':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'cancelled':
      case 'lost':
      case 'damaged':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'rto':
      case 'rto_delivered':
        return <RotateCcw className="w-5 h-5 text-orange-600" />;
      default:
        return <Package className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'picked_up':
      case 'in_transit':
      case 'out_for_delivery':
        return 'bg-blue-100 text-blue-700';
      case 'pending':
      case 'confirmed':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
      case 'lost':
      case 'damaged':
        return 'bg-red-100 text-red-700';
      case 'rto':
      case 'rto_delivered':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleNimbusPickup = async (otp = null) => {
    const id = order?._id || orderId;
    if (!id) return;
    setNimbusNotice(null);
    setNimbusBusy('pickup');
    try {
      const res = await orderService.requestPickup(id, otp);
      
      // Handle OTP requirement
      if (res?.data?.status === 'otp_required' || res?.status === 'otp_required') {
        setOtpModal({ show: true, otp: '' });
        setNimbusNotice({ type: 'ok', text: 'OTP sent to mobile. Please verify.' });
        return;
      }

      // Finalized success
      if (res?.success || res?.awb || res?.status === 'confirmed' || res?.message === 'success') {
        setNimbusNotice({ type: 'ok', text: res.message || 'Shipment booked successfully' });
        setOtpModal({ show: false, otp: '' });
        loadOrder(); // Refresh the page to show AWB and new status
      }
    } catch (err) {
      setNimbusNotice({
        type: 'err',
        text: sanitizeError(err.response?.data?.message || err.message || 'Pickup failed')
      });
    } finally {
      setNimbusBusy(null);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpModal.otp || otpModal.otp.length < 4) {
        alert('Please enter a valid OTP');
        return;
    }
    setVerifying(true);
    await handleNimbusPickup(otpModal.otp);
    setVerifying(false);
  };

  const handleNimbusLabel = async () => {
    const id = order?._id || orderId;
    if (!id) return;
    setNimbusNotice(null);
    setNimbusBusy('label');
    try {
      // Delhivery International and domestic (Delhivery, NimbusPost) labels open
      // inline in a new tab; other partners keep the existing download-to-Disk
      // behavior. Domestic labels are served inline by the backend because S3
      // forces Content-Disposition: attachment, so a direct open can never display them.
      const isIntl = order?.deliveryPartner === 'delhivery_international';
      const isInline = isIntl || order?.deliveryPartner === 'nimbuspost' || order?.deliveryPartner === 'delhivery';
      const result = await orderService.downloadLabel(id, { inline: isInline });
      setNimbusNotice({
        type: 'ok',
        text: result?.inline ? 'Label opened in new tab' : 'Label download started'
      });
    } catch (err) {
      setNimbusNotice({
        type: 'err',
        text: sanitizeError(err.response?.data?.message || err.message || 'Label failed')
      });
    } finally {
      setNimbusBusy(null);
    }
  };

  const handleNimbusCancelShipment = async () => {
    const id = order?._id || orderId;
    if (!id) return;
    if (
      !window.confirm('Cancel this shipment? This action cannot be undone. Continue?')
    ) {
      return;
    }
    setNimbusNotice(null);
    setNimbusBusy('cancel');
    try {
      const res = await orderService.cancelShipment(id);
      setNimbusNotice({
        type: 'ok',
        text:
          res?.message ||
          (typeof res?.data === 'object' && res?.data?.message) ||
          'Shipment cancelled successfully'
      });
      await loadOrder();
    } catch (err) {
      setNimbusNotice({
        type: 'err',
        text: sanitizeError(err.response?.data?.message || err.message || 'Cancel failed')
      });
    } finally {
      setNimbusBusy(null);
    }
  };

  const handlePrintInvoice = () => {
    if (order) {
      generateInvoice(order);
    }
  };

  const handleIntlInvoice = async () => {
    const id = order?._id || orderId;
    if (!id) return;
    setNimbusNotice(null);
    setNimbusBusy('invoice');
    try {
      await orderService.downloadInvoice(id, { inline: true });
      setNimbusNotice({ type: 'ok', text: 'Invoice opened in new tab' });
    } catch (err) {
      setNimbusNotice({
        type: 'err',
        text: sanitizeError(err.response?.data?.message || err.message || 'Invoice failed')
      });
    } finally {
      setNimbusBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error || 'Order not found'}</p>
          <button
            onClick={() => navigate('/orders')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const isPartnerOrder = ['nimbuspost', 'delhivery', 'delhivery_international', 'skart', 'overseas_logistic'].includes(order.deliveryPartner);
  const hasAwb = Boolean(order.awb && String(order.awb).trim());
  const canCancel =
    isPartnerOrder && hasAwb && order.status !== 'cancelled' && order.status !== 'delivered';

  return (
    <div className="min-h-screen mb-24">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="cursor-pointer">
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-[#131842]">Order Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[13fr_7fr] gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          {/* Order Information */}
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#1a2b4b]">Order Information</h2>
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(order.status)}`}>
                {getStatusIcon(order.status)}
                {order.status?.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Order Number</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-blue-600">{order.orderNumber}</p>
                  <button
                    onClick={() => copyToClipboard(order.orderNumber)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">AWB Number</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-[#1a2b4b]">{order.awb || 'Not generated yet'}</p>
                  {order.awb && (
                    <button
                      onClick={() => copyToClipboard(order.awb)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Courier</p>
                <p className="text-sm font-bold text-gray-700">
                  {order.metadata?.courierName ||
                   order.metadata?.nimbusCourierName ||
                   order.metadata?.skartCourierName ||
                   'Assigned on booking'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Order Date</p>
                <p className="text-sm font-bold text-gray-700">{formatDate(order.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Pickup Details */}
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1a2b4b] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-500" />
              Pickup Details
            </h3>
            {order.pickupDetails && (
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-bold text-gray-700">{order.pickupDetails.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-bold text-gray-700">{order.pickupDetails.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="text-sm font-bold text-gray-700">{order.pickupDetails.address}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Pincode</p>
                    <p className="text-sm font-bold text-gray-700">{order.pickupDetails.pincode}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">City</p>
                    <p className="text-sm font-bold text-gray-700">{order.pickupDetails.city}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">State</p>
                    <p className="text-sm font-bold text-gray-700">{order.pickupDetails.state}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Delivery Details */}
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1a2b4b] flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-500" />
              Delivery Details
            </h3>
            {order.deliveryDetails && (
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-bold text-gray-700">{order.deliveryDetails.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-bold text-gray-700">{order.deliveryDetails.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="text-sm font-bold text-gray-700">{order.deliveryDetails.address}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Pincode</p>
                    <p className="text-sm font-bold text-gray-700">{order.deliveryDetails.pincode}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">City</p>
                    <p className="text-sm font-bold text-gray-700">{order.deliveryDetails.city}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">State</p>
                    <p className="text-sm font-bold text-gray-700">{order.deliveryDetails.state}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          {/* Package Details */}
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1a2b4b] flex items-center gap-2">
              <Package className="w-5 h-5" />
              Package Details
            </h3>
            {order.packageDetails && (
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Weight</p>
                  <p className="text-sm font-bold text-gray-700">{order.packageDetails.weight} kg</p>
                </div>
                {order.packageDetails.dimensions && (
                  <div>
                    <p className="text-xs text-gray-500">Dimensions</p>
                    <p className="text-sm font-bold text-gray-700">
                      {order.packageDetails.dimensions.length} × {order.packageDetails.dimensions.width} × {order.packageDetails.dimensions.height} cm
                    </p>
                  </div>
                )}
                {order.packageDetails.description && (
                  <div>
                    <p className="text-xs text-gray-500">Description</p>
                    <p className="text-sm font-bold text-gray-700">{order.packageDetails.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border rounded-lg p-6 space-y-3">
            <h3 className="text-lg font-bold text-[#1a2b4b] flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" />
              Order actions
            </h3>
            {nimbusNotice && (
              <p
                className={`text-xs rounded p-2 ${
                  nimbusNotice.type === 'ok'
                    ? 'bg-green-50 text-green-800 border border-green-100'
                    : 'bg-red-50 text-red-700 border border-red-100'
                }`}
              >
                {nimbusNotice.text}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-1.5 flex-shrink-0">
              {isPartnerOrder && (
                <>
                  <button
                    type="button"
                    onClick={() => handleNimbusPickup()}
                    disabled={
                      !!nimbusBusy ||
                      ['cancelled', 'delivered', 'picked_up'].includes(order.status)
                    }
                    className="inline-flex whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {nimbusBusy === 'pickup' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Truck className="w-4 h-4" />
                    )}
                    Request pickup
                  </button>
                  <button
                    type="button"
                    onClick={handleNimbusLabel}
                    disabled={!!nimbusBusy || !hasAwb}
                    className="inline-flex whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-800 text-sm font-semibold hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {nimbusBusy === 'label' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4" />
                    )}
                    Download label
                  </button>
                  {/* CANCEL BUTTON COMMENTED OUT - RESTORE IF NEEDED
                  <button
                    type="button"
                    onClick={handleNimbusCancelShipment}
                    disabled={!!nimbusBusy || !canCancel}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
                  >
                    {nimbusBusy === 'cancel' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Ban className="w-4 h-4" />
                    )}
                    Cancel shipment
                  </button>
                  END CANCEL BUTTON COMMENT */}
                </>
              )}

              {(order.deliveryPartner === 'delhivery_international' || order.deliveryPartner === 'skart') ? (
                <button
                  type="button"
                  onClick={handleIntlInvoice}
                  disabled={!!nimbusBusy || !order.awb}
                  className="inline-flex whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {nimbusBusy === 'invoice' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Printer className="w-4 h-4" />
                  )}
                  Print Invoice
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePrintInvoice}
                  disabled={order.payment?.status !== 'completed'}
                  className="inline-flex whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Printer className="w-4 h-4" />
                  Print Invoice
                </button>
              )}
              </div>

              {/* ADDED: International contact info */}
              {order.deliveryPartner === 'overseas_logistic' && (
                <>
                  <a href="tel:+918826885557" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:text-blue-600 transition">
                    <Phone className="w-4 h-4" />
                    +918826885557
                  </a>
                  <a
                    href="mailto:support@flywelllogistics.com"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition">
                    <Mail className="w-4 h-4" />
                    Email Us
                  </a>
                </>
              )}

            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1a2b4b]">Pricing</h3>
            {order.pricing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Rate</span>
                  <span className="font-semibold">{formatAmount(order.pricing.baseRate)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Additional Charges</span>
                  <span className="font-semibold">{formatAmount(order.pricing.additionalCharges)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold">Total Amount</span>
                  <span className="font-bold text-lg text-blue-600">{formatAmount(order.pricing.totalAmount)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment Status */}
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1a2b4b]">Payment</h3>
            {order.payment && (
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className={`text-sm font-bold inline-block px-2 py-1 rounded ${
                    order.payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {order.payment.status?.toUpperCase()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Method</p>
                  <p className="text-sm font-bold text-gray-700 capitalize">{order.payment.method || 'Wallet'}</p>
                </div>
                {order.payment.paidAt && (
                  <div>
                    <p className="text-xs text-gray-500">Paid At</p>
                    <p className="text-sm font-bold text-gray-700">{formatDate(order.payment.paidAt)}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tracking */}
          {order.awb && (
            <div className="bg-white border rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-[#1a2b4b]">Tracking</h3>
                <button
                  onClick={handleTrackOrder}
                  disabled={loadingTracking}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loadingTracking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </button>
              </div>
              {tracking && tracking.tracking && (
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Current Status</p>
                    <p className="text-sm font-bold text-gray-700">{tracking.displayStatus || tracking.tracking.status || order.status}</p>
                  </div>
                  {tracking.trackingUrl && (
                    <a
                      href={tracking.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Track on Courier Website →
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* OTP Modal */}
      {otpModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all border border-blue-100 relative">
                {/* Close Button */}
                <button 
                    onClick={() => {
                        setOtpModal({ show: false, otp: '' });
                        setVerifying(false);
                    }}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex flex-col items-center text-center">
                    <div className="bg-blue-50 p-4 rounded-full mb-4">
                        <Truck className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Verify Booking OTP</h3>
                    <p className="text-sm text-gray-500 mb-4">
                        sKart has sent a 6-digit verification code to your registered mobile number.
                        Please enter it to finalize your international shipment.
                    </p>
                    
                    {/* Inline Error Message */}
                    {nimbusNotice && nimbusNotice.type === 'err' && (
                        <div className="w-full p-3 mb-4 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2 animate-pulse">
                            <AlertCircle className="w-4 h-4" />
                            {nimbusNotice.text}
                        </div>
                    )}

                    <div className="w-full space-y-4">
                        <input
                            type="text"
                            maxLength="6"
                            placeholder="Enter 6-digit OTP"
                            value={otpModal.otp}
                            onChange={(e) => setOtpModal({ ...otpModal, otp: e.target.value })}
                            className="w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                        />
                        
                        <div className="flex gap-3">
                            <button
                                onClick={() => setOtpModal({ show: false, otp: '' })}
                                className="flex-1 px-6 py-3 text-sm font-bold text-gray-600 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleVerifyOtp}
                                disabled={verifying || otpModal.otp.length < 4}
                                className="flex-[2] px-6 py-3 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 shadow-lg shadow-blue-200 flex items-center justify-center"
                            >
                                {verifying ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Verify & Book
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default OrderDetailsPage;
